import {
  AnyAssetSubType,
  defaultResponseHandler,
  formatMs,
  FormGroup,
  Grid,
  Switch,
  useAssetTranslation,
  useCustodianQuery,
  useLocationQuery,
  useRolesQuery,
  useUserinfo,
} from '@mss/mss-component-library';
import React, { ReactNode, useState } from 'react';
import { AutoComplete } from 'formik-antd/lib/auto-complete';
import { Input } from 'formik-antd/lib/input';
import { InputNumber } from 'formik-antd/lib/input-number';
import { ErrorMessage, Field, FieldProps, useFormikContext } from 'formik';
import Select from 'formik-antd/lib/select';
import get from 'lodash/get';
import Fetch from '../../util/_fetch';
import { useQuery } from '@tanstack/react-query';
import { Select as AntdSelect } from 'antd';
import useBrandsQuery from '../utils/useBrandsQuery';
import CopyButton from '../Import/CopyButton';
import {
  AutoCompleteInputProps,
  BaseInputProps,
  BaseInputWrapperProps,
  BrandSelectorProps,
  CriticalitySelectorProps,
  CustodianSelectorProps,
  HMCGProps,
  LocationSelectorProps,
  NumberInputProps,
  OPCServerGroupSelectorProps,
  PasswordInputProps,
  RoleSelectorProps,
  SelectInputProps,
  SeriesSelectorProps,
  StringInputProps,
  SwitchInputProps,
  TextInputProps,
  TimezoneSelectorProps,
} from './inputTypes';
import { enterNextStep, fieldNameToFormName } from './inputUtil';

const BaseInputWrapper = ({
  width,
  required,
  label,
  children,
  index,
  fieldName,
  copyButton,
  subType,
}: BaseInputWrapperProps) => {
  const { t } = useAssetTranslation();
  return (
    <Grid.Col width={width ?? 4}>
      <FormGroup
        actions={
          copyButton && (
            <CopyButton index={index} fieldName={fieldName} subType={subType} />
          )
        }
        required={required}
        label={label as any}
        input={children}
        error={
          <ErrorMessage
            name={fieldNameToFormName(fieldName, index)}
            render={msg => t(msg)}
          />
        }
      />
    </Grid.Col>
  );
};

type DefaultPropsConfig = {
  disableOnBlur?: boolean;
  disableOnKeyPress?: boolean;
  undefinedValue?: boolean;
};
const useDefaultInputProps = (
  props: BaseInputProps,
  config?: DefaultPropsConfig
) => {
  const { t } = useAssetTranslation();
  const { values, handleBlur } = useFormikContext<any>();
  const fullFieldName = fieldNameToFormName(props.fieldName, props.index);
  const label: string | ReactNode = props.customLabel ?? t(props.label);
  const placeholder = props.placeholder ?? t(props.label);
  return {
    wrapper: {
      width: props.compact ? (props.compactWidth ?? 4) : (props.width ?? 4),
      required: props.required,
      label,
      index: props.index,
      fieldName: props.fieldName,
      copyButton: props.copyButton,
      subType: props.subType,
    },
    input: {
      fullFieldName,
      disabled: props.disabled,
      name: fullFieldName,
      placeholder,
      style: { width: '100%' },
      value: values.assets[props.index ?? 0].values[props.fieldName],
      'data-ta': fullFieldName,
      ...(config?.disableOnBlur ? {} : { onBlur: handleBlur }),
      ...(config?.disableOnKeyPress
        ? {}
        : { onKeyPress: enterNextStep(props.nextStep, props.nextStepBlocked) }),
    },
  };
};

export const SwitchInput = (props: SwitchInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();

  return (
    <BaseInputWrapper {...wrapper}>
      <Switch
        {...input}
        checked={input.value}
        onChange={async checked => {
          await setFieldValue(fullFieldName, checked, false);
          await setFieldTouched(fullFieldName, true, true);
        }}
        style={{ width: 'unset' }}
        data-ta={fullFieldName}
      />
    </BaseInputWrapper>
  );
};

export const StringInput = (props: StringInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldTouched } = useFormikContext<any>();
  const { t } = useAssetTranslation();

  return (
    <BaseInputWrapper {...wrapper}>
      <Input
        {...input}
        placeholder={t(input.placeholder)}
        maxLength={props.maxLength}
        onInput={async () => {
          await setFieldTouched(fullFieldName, true, true);
        }}
        data-ta={fullFieldName}
        onKeyDown={enterNextStep(props.nextStep, props.nextStepBlocked)}
      />
    </BaseInputWrapper>
  );
};

export const NumberInput = (props: NumberInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldTouched, setFieldValue } = useFormikContext<any>();
  const { t } = useAssetTranslation();

  return (
    <BaseInputWrapper {...wrapper}>
      <InputNumber
        {...input}
        placeholder={t(input.placeholder)}
        min={props.min}
        max={props.max}
        onInput={value => {
          if (value) {
            setFieldValue(fullFieldName, value);
            setFieldTouched(fullFieldName, true, false);
          }
        }}
      />
    </BaseInputWrapper>
  );
};

export const PasswordInput = (props: PasswordInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldTouched, values } = useFormikContext<any>();
  const { t } = useAssetTranslation();
  const hasId = values.assets[props.index ?? 0].values.id;
  return (
    <BaseInputWrapper {...wrapper}>
      <Input.Password
        {...input}
        autoComplete={hasId ? 'current-password' : 'new-password'}
        placeholder={t(input.placeholder)}
        type="password"
        maxLength={props.maxLength}
        onInput={async () => {
          await setFieldTouched(fullFieldName, true, true);
        }}
        data-ta={fullFieldName}
        onKeyDown={enterNextStep(props.nextStep, props.nextStepBlocked)}
      />
    </BaseInputWrapper>
  );
};

export const TextAreaInput = (props: TextInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldTouched } = useFormikContext<any>();
  const { t } = useAssetTranslation();

  return (
    <BaseInputWrapper {...wrapper}>
      <Input.TextArea
        {...input}
        placeholder={t(input.placeholder)}
        rows={props.rows}
        maxLength={props.maxLength}
        onInput={async () => {
          await setFieldTouched(fullFieldName, true, true);
        }}
        data-ta={fullFieldName}
        onKeyPress={enterNextStep(props.nextStep, props.nextStepBlocked)}
      />
    </BaseInputWrapper>
  );
};

export const SelectInput = (props: SelectInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { setFieldTouched } = useFormikContext<any>();
  const { t } = useAssetTranslation();

  return (
    <BaseInputWrapper {...wrapper}>
      <Select
        {...input}
        onChange={props.onChange}
        onSelect={async () => {
          await setFieldTouched(fullFieldName, true, false);
        }}
        placeholder={t(input.placeholder)}
        showSearch={props.showSearch ?? true}
        options={props.options}
        value={props.value || input.value || undefined}
        filterOption={(inputValue, option) => {
          return option?.props?.label
            ?.toString()
            .toLowerCase()
            .includes(inputValue.toLowerCase());
        }}
      />
    </BaseInputWrapper>
  );
};

export const AutoCompleteInput = (props: AutoCompleteInputProps) => {
  const {
    wrapper,
    input: { fullFieldName, ...input },
  } = useDefaultInputProps(props);
  const { t } = useAssetTranslation();

  return (
    <BaseInputWrapper {...wrapper}>
      <AutoComplete
        {...input}
        placeholder={t(input.placeholder)}
        style={{ width: '100%' }}
        options={props.options}
        filterOption
        value={props.value ?? input.value}
      />
    </BaseInputWrapper>
  );
};

export const CustodianSelector = (props: CustodianSelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps(props);

  const { values } = useFormikContext<any>();
  const { data } = useCustodianQuery();
  const value = get(values, fullFieldName);

  return (
    <SelectInput
      {...props}
      index={props.index}
      required
      label={t('Custodian')}
      fieldName={'custodian'}
      options={data}
      placeholder={t('Please select one...')}
      value={String(value?.id ?? value) || undefined}
    />
  );
};

export const CriticalitySelector = (props: CriticalitySelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps(props);

  const { values } = useFormikContext<any>();
  const data = [
    { label: t('Low'), value: 'low' },
    { label: t('Medium'), value: 'medium' },
    { label: t('High'), value: 'high' },
  ];
  const value = get(values, fullFieldName);

  return (
    <SelectInput
      {...props}
      required
      placeholder={t('Please select one...')}
      label={t('Criticality')}
      fieldName={'priority'}
      options={data}
      value={value || undefined}
    />
  );
};

export const RoleSelector = (props: RoleSelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps(props);

  const { values } = useFormikContext<any>();
  const { data } = useRolesQuery();
  const value = get(values, fullFieldName);

  return (
    <AutoCompleteInput
      {...props}
      required
      label={t('Role')}
      fieldName={'role'}
      placeholder={t('Enter or select a role')}
      options={data}
      value={String(value?.location ?? value) || undefined}
    />
  );
};

export const LocationSelector = (props: LocationSelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps(props);

  const { values } = useFormikContext<any>();
  const { data } = useLocationQuery();
  const value = get(values, fullFieldName);

  return (
    <AutoCompleteInput
      {...props}
      required
      label={t('Location')}
      placeholder={t('Enter or select a location')}
      fieldName={'location'}
      options={data}
      value={String(value?.location ?? value) || undefined}
    />
  );
};

export const TimezoneSelector = (props: TimezoneSelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps(props);

  const { values } = useFormikContext<any>();
  const { data } = useQuery({
    queryKey: ['timezones'],
    initialData: [],
    cacheTime: formatMs({ hours: 1 }),
    staleTime: formatMs({ hours: 1 }),
    queryFn: async () =>
      (
        await defaultResponseHandler<string[]>({
          t,
          request: Fetch.get('/api/timezones/'),
        })
      ).data.map(tz => ({ label: tz.replace('_', ' '), value: tz })) ?? [],
  });
  const value = get(values, fullFieldName);

  return (
    <SelectInput
      {...props}
      required
      label={t('Timezone')}
      placeholder={t('Select a timezone')}
      fieldName={'timezone'}
      options={data}
      value={String(value?.timezone ?? value) || undefined}
    />
  );
};

type OPCServerOption = { id: string; name: string };

export const OPCServerGroupSelector = (props: OPCServerGroupSelectorProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
    wrapper: { fieldName },
  } = useDefaultInputProps(props);

  const { values, setFieldValue } = useFormikContext<any>();
  const [hasPermission] = useUserinfo();
  const hasOpcPermission = hasPermission('view_opcservergroupconfig');

  React.useEffect(() => {
    if (!hasOpcPermission) {
      setFieldValue(fullFieldName, null);
      return;
    }
    const rawValue = get(values, fullFieldName);
    if (rawValue !== null && rawValue !== undefined && typeof rawValue === 'object') {
      setFieldValue(fullFieldName, rawValue.id ? String(rawValue.id) : '');
    }
  }, [hasOpcPermission]);

  const { data } = useQuery({
    queryKey: ['opc-config-server-group'],
    initialData: [] as { label: string; value: string }[],
    enabled: hasOpcPermission,
    queryFn: async () => {
      const res = await defaultResponseHandler<OPCServerOption[]>({
        t,
        request: Fetch.get('/api/opc-config-server-group/'),
      });
      const list = Array.isArray(res?.data) ? res.data : (res as any)?.results ?? [];
      return list.map((item: OPCServerOption) => ({
        label: item.name || item.id,
        value: item.id,
      }));
    },
  });

  const rawValue = get(values, fullFieldName);
  const value = hasOpcPermission
    ? String(rawValue?.id ?? rawValue ?? '') || undefined
    : undefined;

  return (
    <SelectInput
      {...props}
      required={hasOpcPermission ? (props.required ?? true) : false}
      disabled={!hasOpcPermission || props.disabled}
      label={t('OPC Server Group')}
      fieldName={fieldName}
      placeholder={t('Select OPC Server Group')}
      options={data}
      value={value}
    />
  );
};

export const HistoricalMessagesCollectionGroup = (props: HMCGProps) => {
  const { t } = useAssetTranslation();
  const {
    input: { fullFieldName },
  } = useDefaultInputProps({ ...props, fieldName: 'ip_addresses' });
  const [searchValue, setSearchValue] = useState('');
  const { values } = useFormikContext<any>();
  const baseValue = get(values, fullFieldName, []);
  const currentValue = Array.isArray(baseValue)
    ? baseValue
    : baseValue?.split(';').filter(Boolean) || [];

  return (
    <Grid.Col width={8}>
      <FormGroup
        label={t('Historical Messages Collection Group')}
        actions={
          props.copyButton && (
            <CopyButton
              index={props.index}
              fieldName={'ip_addresses'}
              subType={props.subType}
            />
          )
        }
        input={
          <Field name={fullFieldName}>
            {({
              field: { value, name: fieldName },
              form: { setFieldValue, setFieldTouched, handleSubmit },
            }: FieldProps) => {
              return (
                <AntdSelect
                  searchValue={searchValue}
                  mode="tags"
                  style={{ width: '100%' }}
                  value={value}
                  placeholder={t('Enter one or more IP addresses')}
                  options={[]}
                  onSearch={setSearchValue}
                  disabled={props.disabled}
                  onSelect={async (newValue: string) => {
                    await setFieldTouched(fieldName, true, true);
                    await setFieldValue(fieldName, [...currentValue, newValue]);
                  }}
                  onInputKeyDown={
                    searchValue
                      ? e => {
                          if (
                            [' ', ';', ',', 'Enter', 'Tab'].findIndex(
                              k => k === e.key
                            ) > -1
                          ) {
                            if (e.key !== 'Tab') e.preventDefault();
                            setFieldValue(fieldName, [
                              ...currentValue,
                              searchValue,
                            ]);
                            setSearchValue('');
                          }
                        }
                      : (enterNextStep?.(() => null, props.disabled) ??
                        (e => e.key === 'Enter' && handleSubmit()))
                  }
                  onBlur={() => setFieldTouched(fieldName, true, false)}
                  onDeselect={async (deselected: string) => {
                    await setFieldTouched(fieldName, true, false);
                    await setFieldValue(
                      fieldName,
                      currentValue.filter((x: string) => x !== deselected)
                    );
                  }}
                  onClear={async () => {
                    await setFieldTouched(fieldName, true, false);
                    await setFieldValue(fieldName, []);
                  }}
                  open={false}
                />
              );
            }}
          </Field>
        }
        error={
          <ErrorMessage
            name={fullFieldName}
            render={msg =>
              Array.isArray(msg) &&
              msg.length > 0 &&
              t('Please enter valid IP addresses')
            }
          />
        }
      />
    </Grid.Col>
  );
};

export const BrandSelector = (props: BrandSelectorProps) => {
  const { t } = useAssetTranslation();
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();

  const { data } = useBrandsQuery(
    props.subType
      ? { assetSubType: props.subType.value as AnyAssetSubType }
      : undefined
  );
  const options = data.map(brand => ({ value: brand.id, label: brand.name }));
  return (
    <SelectInput
      index={props.index}
      required
      label={t('Brand')}
      placeholder={t('Please select one') + '...'}
      fieldName={'brand'}
      options={options}
      disabled={props.disabled}
      onChange={async id => {
        await setFieldValue(
          fieldNameToFormName('series', props.index),
          data.find(brand => brand.id === id)?.series_set[0].id ?? '',
          false
        );
        await setFieldTouched(
          fieldNameToFormName('series', props.index),
          true,
          true
        );
      }}
    />
  );
};

export const SeriesSelector = (props: SeriesSelectorProps) => {
  const { t } = useAssetTranslation();

  const { values } = useFormikContext<any>();
  const { data } = useBrandsQuery(
    props.subType
      ? { assetSubType: props.subType.value as AnyAssetSubType }
      : undefined
  );
  const brandIdValue = get(values, fieldNameToFormName('brand', props.index));
  const matchingBrand = data.find(brand => brand.id === brandIdValue);
  const series = matchingBrand?.series_set ?? [];

  return (
    <SelectInput
      index={props.index}
      required
      label={t('Model / Series')}
      placeholder={t('Please select one') + '...'}
      fieldName={'series'}
      disabled={props.disabled}
      options={series.map(series => ({ value: series.id, label: series.name }))}
    />
  );
};
